import styled, { css, ThemeProvider } from 'styled-components';
import type { TopBannerModel } from 'autogen/swagger/all';
import { breakpoint, sizes } from 'variables';
import Disclaimer from 'components/topBanners/disclaimer';
import { toHtmlContent } from 'modules/helpers/strings';
import {
    BorderRadiusMedium,
    FontCaptionFamily,
    FontCaptionWeightSemiBold,
    FontBody2WeightRegular,
    FontBody1LineHeight,
    Spacing16,
    Spacing12,
    Spacing8,
    Spacing4,
    FontSize12,
    ColorNordicGrey1
} from 'autogen/design-tokens/tokens';
import { getTheme, themed } from 'helpers/designThemes';
import PageAction from 'components/shared/Routing/PageAction';
import type { ColorTheme } from 'types/tokens';

const TopBanner = styled.div<{ singleBanner: boolean }>`
    flex-shrink: 0;
    padding: 0 ${Spacing4};
    flex: 1 0 auto;
    min-width: 50%;

    &:first-child {
        min-width: 65%;
    }

    ${breakpoint.up(sizes.sm)} {
        &:first-child {
            min-width: 50%;
        }
    }

    //Desktop
    ${breakpoint.up(sizes.xl)} {
        padding: 0 6px;
        flex: ${({ singleBanner }): string => (singleBanner ? '0 0 100%' : '0 0 50%')};
    }
`;

const topBannerInnerStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${BorderRadiusMedium};
    padding: ${Spacing12};
    height: 40px;
    text-decoration: none !important;
    background-color: ${({ theme }) => theme.color1};
    width: 100%;


    //Desktop
    ${breakpoint.up(sizes.xl)} {
        flex-direction: column;
        height: 48px;
        padding: ${Spacing8} ${Spacing16};
    }
`;

const TopBannerInnerPageAction = styled(PageAction)`
    ${topBannerInnerStyles}
`;

const TopBannerInnerStatic = styled.div`
    ${topBannerInnerStyles}
`;

const Text = styled.div<{ type: TopBannerModel['type'] }>`
    font-size: ${FontSize12};
    font-weight: ${FontBody2WeightRegular};
    line-height: ${FontBody1LineHeight};
    text-align: center;

    & b {
        font-weight: ${FontCaptionWeightSemiBold};
    }
`;

const MobileText = styled(Text)`
    display: flex;

    ${breakpoint.up(sizes.xl)} {
        display: none;
    }
`;

const DesktopText = styled(Text)`
    display: none;

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        align-items: center;
    }
`;

const CallToActionText = styled.span<{ type: TopBannerModel['type'] }>`
    display: none;
    font-family: ${FontCaptionFamily};
    font-weight: ${FontCaptionWeightSemiBold};
    font-size: ${FontSize12};
    text-decoration: underline;

    //Desktop
    ${breakpoint.up(sizes.xl)} {
        display: block;
    }
`;

interface ITopBannerModel extends TopBannerModel {
    singleBanner?: boolean;
}

function TopBannerItem(props: ITopBannerModel): JSX.Element {
    const {
        dataLayerDto,
        disclaimer,
        ctaText,
        pageAction,
        mobileText,
        text,
        type,
        designThemeName,
        singleBanner = false,
        ...restProps
    } = props;

    let theme = designThemeName ? getTheme(designThemeName as ColorTheme) : undefined;

    if (!theme?.color1 && !theme?.color5) {
        theme = { color1: ColorNordicGrey1 };
    }

    const content = (
        <>
            <MobileText type={type}>{toHtmlContent(mobileText)}</MobileText>
            <DesktopText type={type}>
                {toHtmlContent(text)}
                {disclaimer && <Disclaimer disclaimer={disclaimer} />}
            </DesktopText>
            {ctaText && <CallToActionText type={type}>{ctaText}</CallToActionText>}
        </>
    );

    return (
        <ThemeProvider theme={theme}>
            <TopBanner singleBanner={singleBanner} data-datalayer={JSON.stringify(dataLayerDto)} {...restProps}>
                {pageAction ? (
                    <TopBannerInnerPageAction action={pageAction}>{content}</TopBannerInnerPageAction>
                ) : (
                    <TopBannerInnerStatic>{content}</TopBannerInnerStatic>
                )}
            </TopBanner>
        </ThemeProvider>
    );
}

export default themed(TopBannerItem, { color: 'NeutralGrey' }, (props) => props.designThemeName as ColorTheme);
